// Путь: D:\QR\QR-WEB\dashboard\src\components\Admin\AdminCampaigns\AdminCampaigns.jsx
// Название: Admin Campaigns List
// Версия: 2.0.0
// Дата: 01.02.2026

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './AdminCampaigns.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://qr-money.onrender.com';

const AdminCampaigns = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  
  const [filters, setFilters] = useState({
    status: searchParams.get('status') || '',
    search: searchParams.get('search') || ''
  });
  
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  const token = localStorage.getItem('authToken');

  useEffect(() => {
    loadCampaigns();
  }, [filters]);

  const loadCampaigns = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('type', 'brand'); // Только Brand кампании
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);
      params.append('limit', '50');
      
      const res = await fetch(`${API_URL}/api/admin/campaigns?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setCampaigns(data.data || []);
      setPagination(data.pagination || { page: 1, pages: 1, total: 0 });
    } catch (err) {
      console.error('Load campaigns error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([k, v]) => {
      if (v) params.set(k, v);
    });
    setSearchParams(params);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  // Сортировка на клиенте
  const getSortedCampaigns = () => {
    return [...campaigns].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (['budget_dar', 'gifts_count', 'claimed_count', 'recipients_count', 'id'].includes(sortField)) {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }
      if (sortField === 'created_at') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const sortedCampaigns = getSortedCampaigns();

  // Статистика
  const stats = {
    total: campaigns.length,
    active: campaigns.filter(c => c.status === 'active').length,
    completed: campaigns.filter(c => c.status === 'completed').length,
    draft: campaigns.filter(c => c.status === 'draft').length,
    totalGifts: campaigns.reduce((sum, c) => sum + parseInt(c.gifts_count || 0), 0),
    claimedGifts: campaigns.reduce((sum, c) => sum + parseInt(c.claimed_count || 0), 0),
    totalBudget: campaigns.reduce((sum, c) => sum + parseFloat(c.budget_dar || 0), 0)
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Удалить кампанию "${name}"?`)) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/campaigns/${id}?force=true`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (data.success) {
        loadCampaigns();
      } else {
        alert(data.error || 'Ошибка удаления');
      }
    } catch (err) {
      alert('Ошибка: ' + err.message);
    }
  };

  const handleComplete = async (id) => {
    if (!window.confirm('Завершить кампанию?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/campaigns/${id}/complete`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (data.success) {
        loadCampaigns();
      } else {
        alert(data.error || 'Ошибка');
      }
    } catch (err) {
      alert('Ошибка: ' + err.message);
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Отменить кампанию?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/campaigns/${id}/cancel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      
      if (data.success) {
        loadCampaigns();
      } else {
        alert(data.error || 'Ошибка отмены');
      }
    } catch (err) {
      alert('Ошибка: ' + err.message);
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      draft: { label: 'Черновик', className: 'badge-secondary' },
      ready: { label: 'Готова', className: 'badge-primary' },
      active: { label: 'Активна', className: 'badge-success' },
      generating: { label: 'Генерация', className: 'badge-warning' },
      completed: { label: 'Завершена', className: 'badge-purple' },
      cancelled: { label: 'Отменена', className: 'badge-danger' }
    };
    return map[status] || { label: status, className: 'badge-secondary' };
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('ru-RU') : '-';
  
  const formatDar = (amount) => parseFloat(amount || 0).toFixed(0) + ' DAR';

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span className="sort-icon">↕️</span>;
    return <span className="sort-icon">{sortOrder === 'asc' ? '⬆️' : '⬇️'}</span>;
  };

  // Экспорт
  const exportToCSV = () => {
    const headers = ['ID', 'Название', 'Тип', 'Бизнес', 'Статус', 'Получатели', 'Подарков', 'Claimed', 'Бюджет DAR', 'Дата'];
    const rows = sortedCampaigns.map(c => [
      c.id,
      c.name || '-',
      c.campaign_type || 'brand_giveaway',
      c.business_email || '-',
      c.status,
      c.recipients_count || 0,
      c.gifts_count || 0,
      c.claimed_count || 0,
      c.budget_dar || 0,
      formatDate(c.created_at)
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `campaigns_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  if (loading && campaigns.length === 0) {
    return <div className="admin-campaigns"><div className="loading-state">⏳ Загрузка...</div></div>;
  }

  return (
    <div className="admin-campaigns">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <h1>📋 Brand Кампании</h1>
          <p>Все Brand Giveaway кампании • Показано: {sortedCampaigns.length}</p>
        </div>
        <div className="header-actions">
          <button className="btn-export" onClick={exportToCSV}>📥 Экспорт</button>
          <button className="btn-refresh" onClick={loadCampaigns}>🔄 Обновить</button>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card" onClick={() => handleFilterChange('status', '')}>
          <div className="stat-icon">📋</div>
          <div className="stat-content">
            <div className="stat-value">{stats.total}</div>
            <div className="stat-label">Всего</div>
          </div>
        </div>
        <div className="stat-card green" onClick={() => handleFilterChange('status', 'active')}>
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <div className="stat-value">{stats.active}</div>
            <div className="stat-label">Активных</div>
          </div>
        </div>
        <div className="stat-card purple" onClick={() => handleFilterChange('status', 'completed')}>
          <div className="stat-icon">🏁</div>
          <div className="stat-content">
            <div className="stat-value">{stats.completed}</div>
            <div className="stat-label">Завершено</div>
          </div>
        </div>
        <div className="stat-card orange">
          <div className="stat-icon">🎁</div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalGifts}</div>
            <div className="stat-label">Подарков</div>
            <div className="stat-detail">{stats.claimedGifts} claimed</div>
          </div>
        </div>
        <div className="stat-card blue">
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalBudget.toFixed(0)}</div>
            <div className="stat-label">Бюджет DAR</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-row">
        <input
          type="text"
          placeholder="🔍 Поиск по названию или email..."
          value={filters.search}
          onChange={(e) => handleFilterChange('search', e.target.value)}
          className="search-input"
        />
        <select 
          value={filters.status} 
          onChange={(e) => handleFilterChange('status', e.target.value)}
          className="filter-select"
        >
          <option value="">Все статусы</option>
          <option value="draft">📝 Черновик ({stats.draft})</option>
          <option value="active">✅ Активна ({stats.active})</option>
          <option value="generating">⏳ Генерация</option>
          <option value="completed">🏁 Завершена ({stats.completed})</option>
          <option value="cancelled">❌ Отменена</option>
        </select>
      </div>

      {/* Table */}
      <div className="table-container">
        <table className="campaigns-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('id')} className="sortable">
                ID <SortIcon field="id" />
              </th>
              <th onClick={() => handleSort('name')} className="sortable">
                Название <SortIcon field="name" />
              </th>
              <th>Бизнес</th>
              <th onClick={() => handleSort('status')} className="sortable">
                Статус <SortIcon field="status" />
              </th>
              <th onClick={() => handleSort('gifts_count')} className="sortable">
                Подарков <SortIcon field="gifts_count" />
              </th>
              <th onClick={() => handleSort('claimed_count')} className="sortable">
                Claimed <SortIcon field="claimed_count" />
              </th>
              <th onClick={() => handleSort('budget_dar')} className="sortable">
                Бюджет <SortIcon field="budget_dar" />
              </th>
              <th onClick={() => handleSort('created_at')} className="sortable">
                Дата <SortIcon field="created_at" />
              </th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {sortedCampaigns.map(c => {
              const status = getStatusBadge(c.status);
              const claimRate = c.gifts_count > 0 
                ? Math.round((c.claimed_count / c.gifts_count) * 100) 
                : 0;
              
              return (
                <tr key={c.id} className={c.status === 'cancelled' ? 'row-cancelled' : ''}>
                  <td className="id-cell">#{c.id}</td>
                  <td className="name-cell">
                    <span className="campaign-name" onClick={() => navigate(`/admin/campaigns/${c.id}`)}>
                      {c.name || 'Без названия'}
                    </span>
                    <span className="campaign-subtype">{c.campaign_type || 'brand_giveaway'}</span>
                  </td>
                  <td className="email-cell">
                    <div>{c.company_name || '-'}</div>
                    <div className="email-sub">{c.business_email}</div>
                  </td>
                  <td>
                    <span className={`badge ${status.className}`}>{status.label}</span>
                  </td>
                  <td className="num-cell">{c.gifts_count || 0}</td>
                  <td className="num-cell">
                    <span className={claimRate > 50 ? 'text-success' : ''}>{c.claimed_count || 0}</span>
                    {c.gifts_count > 0 && <span className="claim-rate">({claimRate}%)</span>}
                  </td>
                  <td className="num-cell">{formatDar(c.budget_dar)}</td>
                  <td className="date-cell">{formatDate(c.created_at)}</td>
                  <td className="actions-cell">
                    <button className="btn-action btn-view" onClick={() => navigate(`/admin/campaigns/${c.id}`)} title="Детали">👁️</button>
                    {c.status === 'active' && (
                      <button className="btn-action btn-complete" onClick={() => handleComplete(c.id)} title="Завершить">✅</button>
                    )}
                    {!['completed', 'cancelled'].includes(c.status) && (
                      <button className="btn-action btn-cancel" onClick={() => handleCancel(c.id)} title="Отменить">⛔</button>
                    )}
                    <button className="btn-action btn-delete" onClick={() => handleDelete(c.id, c.name)} title="Удалить">🗑️</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        
        {sortedCampaigns.length === 0 && (
          <div className="empty-state">Нет кампаний по выбранным фильтрам</div>
        )}
      </div>

      {pagination.total > 0 && (
        <div className="pagination-info">
          Показано {sortedCampaigns.length} из {pagination.total}
        </div>
      )}
    </div>
  );
};

export default AdminCampaigns;